import { useLocation } from 'react-router-dom'

import { Breadcrumbs, type Crumb } from '../ui/breadcrumbs'

/** Words for the path segments the console uses.
 *
 * A lookup rather than a prettified segment, because "cab" should read as "CAB"
 * and "kb" as "Knowledge base", which no amount of capitalisation gets right.
 */
const SEGMENT_LABELS: Record<string, string> = {
  agent: 'Console',
  tickets: 'Tickets',
  incidents: 'Incidents',
  requests: 'Service requests',
  problems: 'Problems',
  changes: 'Changes',
  cab: 'CAB',
  assets: 'Assets',
  kb: 'Knowledge base',
  catalog: 'Service catalog',
  inventory: 'Inventory',
  lifecycle: 'Types & lifecycle',
  discovery: 'Discovery',
  executive: 'Executive overview',
  reports: 'Reports',
  automation: 'Automation',
  designer: 'Designer',
  templates: 'Templates',
  administration: 'Administration',
  settings: 'Settings',
}

/** Segments that are containers, not destinations — never linked. */
const NOT_NAVIGABLE = new Set(['agent'])

function labelFor(segment: string): string {
  const known = SEGMENT_LABELS[segment]
  if (known) return known
  // A record reference such as INC-000021 or CHG-000002 is already the clearest
  // label it could have, so it is left exactly as it is.
  return segment
}

export function RouteBreadcrumbs() {
  const { pathname } = useLocation()
  const segments = pathname.split('/').filter(Boolean)

  // The dashboard is the root of the console; a lone "Console" crumb pointing
  // at the page you are on is noise.
  if (segments.length <= 1) return null

  const crumbs: Crumb[] = segments.map((segment, index) => {
    const to = '/' + segments.slice(0, index + 1).join('/')
    const isLast = index === segments.length - 1
    return {
      label: labelFor(segment),
      to: isLast || NOT_NAVIGABLE.has(segment) ? undefined : to,
    }
  })

  // "Console" is the dashboard, and is worth linking even though the segment
  // itself is a container.
  crumbs[0] = { label: 'Dashboard', to: '/agent' }

  return <Breadcrumbs items={crumbs} />
}
